import React from 'react';
import { LoadingOverlay, Skeleton } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { Button } from '~/domains/design-system/Button';

import dt from '../../../testing.const';

import type useStyles from './EnterContestScreen.styles';

export type CTAVariant = 'error' | 'deposit' | 'enter';

type ContestCTAProps = {
  variant: CTAVariant;
  isCreateEntriesReady: boolean;
  isZipGateUnlocked: boolean;
  isEntertainmentOnly: boolean;
  value: number;
  totalEntryFee: number;
  classes: ReturnType<typeof useStyles>['classes'];
  onDepositClick: () => void;
  onCreateEntries: () => void;
  onClose: () => void;
};

function ContestCTA({
  variant,
  isCreateEntriesReady,
  isZipGateUnlocked,
  isEntertainmentOnly,
  value,
  totalEntryFee,
  classes,
  onDepositClick,
  onCreateEntries,
  onClose,
}: ContestCTAProps) {
  const { t } = useTranslation('contest');

  // Entry fee can be zero or negative if voucher is applied. Always show "Free" in this case
  const entryFeeText = totalEntryFee <= 0
    ? t('common.enterContestModal.enterContest.free')
    : CurrencyConvertor(totalEntryFee)
  ;

  return match(variant)
    .returnType<React.ReactNode>()
    .with('error', () => (
      <Button
        variant="secondary-fill"
        className={classes.cta}
        onClick={onClose}
        data-test-id={dt.enterContestModal.action.cancel}
      >
        {t('common.enterContestModal.enterContest.close')}
      </Button>
    ))
    .with('deposit', () => (
      <Skeleton visible={!isCreateEntriesReady} className={classes.ctaWrapper}>
        <LoadingOverlay visible={!isCreateEntriesReady} />
        <Button
          variant="secondary-fill"
          className={classes.cta}
          onClick={onDepositClick}
          data-test-id={dt.enterContestModal.action.depositToEnter}
        >
          {t('common.enterContestModal.enterContest.depositToEnter')}
        </Button>
      </Skeleton>
    ))
    .with('enter', () => (
      <Skeleton visible={!isCreateEntriesReady} className={classes.ctaWrapper}>
        <LoadingOverlay visible={!isCreateEntriesReady} />
        <Button
          variant="primary-fill"
          className={classes.cta}
          onClick={onCreateEntries}
          data-test-id={dt.enterContestModal.action.createEntries}
          disabled={!value || !isCreateEntriesReady || !isZipGateUnlocked}
        >
          {isEntertainmentOnly
            ? t('common.enterContestModal.enterContest.addEntriesEntertainmentOnly__hasPlural', {
                count: value,
              })
            : t('common.enterContestModal.enterContest.addEntries__hasPlural', {
                count: value,
                entryFee: entryFeeText,
              })}
        </Button>
      </Skeleton>
    ))
    .exhaustive();
}

export default ContestCTA;
